import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface SuccessScreenProps {
  orderId: string;
  totalPoints: number;
  remainPoints: number;
  recipientName: string;
  recipientPhone: string;
}

const SuccessScreen = ({ orderId, totalPoints, remainPoints, recipientName, recipientPhone }: SuccessScreenProps) => {
  const navigate = useNavigate();
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 300);
    const t2 = setTimeout(() => setPhase(2), 700);
    const t3 = setTimeout(() => setPhase(3), 900);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  const particles = Array.from({ length: 12 }, (_, i) => ({
    angle: (i / 12) * 360,
    delay: i * 40,
    color: i % 3 === 0 ? '#10b981' : i % 3 === 1 ? '#f59e0b' : '#6ee7b7',
    size: i % 2 === 0 ? 8 : 5,
  }));

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex flex-col items-center justify-center max-w-[480px] mx-auto px-6 overflow-hidden pb-10">

      {/* 动画圆圈 + 对勾 */}
      <div className="relative flex items-center justify-center mb-8" style={{ width: 120, height: 120 }}>
        <div
          className="absolute rounded-full bg-emerald-100"
          style={{
            width: phase >= 0 ? 120 : 0,
            height: phase >= 0 ? 120 : 0,
            transition: 'all 0.4s cubic-bezier(0.34,1.56,0.64,1)',
            opacity: phase >= 2 ? 0.6 : 1,
          }}
        />
        <div
          className="absolute rounded-full bg-emerald-500"
          style={{
            width: phase >= 1 ? 80 : 0,
            height: phase >= 1 ? 80 : 0,
            transition: 'all 0.35s cubic-bezier(0.34,1.56,0.64,1)',
          }}
        />
        <div
          style={{
            opacity: phase >= 1 ? 1 : 0,
            transform: phase >= 1 ? 'scale(1) rotate(0deg)' : 'scale(0) rotate(-45deg)',
            transition: 'all 0.4s cubic-bezier(0.34,1.56,0.64,1) 0.15s',
            position: 'relative',
            zIndex: 10,
          }}
        >
          <i className="ri-check-line text-white text-4xl" />
        </div>
        {phase >= 3 && particles.map((p, i) => (
          <div
            key={i}
            className="absolute rounded-full"
            style={{
              width: p.size,
              height: p.size,
              backgroundColor: p.color,
              top: '50%',
              left: '50%',
              transform: `translate(-50%, -50%) rotate(${p.angle}deg) translateY(-60px)`,
              opacity: 0,
              animation: `particleFly 0.8s ease-out ${p.delay}ms forwards`,
            }}
          />
        ))}
      </div>

      {/* 文字内容 */}
      <div
        style={{
          opacity: phase >= 2 ? 1 : 0,
          transform: phase >= 2 ? 'translateY(0)' : 'translateY(20px)',
          transition: 'all 0.5s ease 0.1s',
          width: '100%',
        }}
      >
        <div className="text-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">兑换成功！</h2>
          <p className="text-gray-500 text-sm">您的兑换订单已提交，我们将尽快为您安排发货</p>
        </div>

        {/* 订单信息卡片 */}
        <div className="bg-white rounded-2xl p-5 mb-4 w-full space-y-3.5">
          {[
            { label: '订单编号', value: orderId, icon: 'ri-file-list-3-line' },
            { label: '收货人', value: `${recipientName}  ${recipientPhone.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')}`, icon: 'ri-user-line' },
            { label: '消耗积分', value: `${totalPoints.toLocaleString()} 积分`, icon: 'ri-copper-coin-line', highlight: true },
            { label: '剩余积分', value: `${remainPoints.toLocaleString()} 积分`, icon: 'ri-wallet-3-line' },
            { label: '预计送达', value: '3-5个工作日', icon: 'ri-truck-line' },
          ].map((item) => (
            <div key={item.label} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className={`${item.icon} text-gray-400 text-sm`} />
                </div>
                <span className="text-sm text-gray-500">{item.label}</span>
              </div>
              <span className={`text-sm font-medium ${item.highlight ? 'text-emerald-600 font-bold text-base' : 'text-gray-800'}`}>
                {item.value}
              </span>
            </div>
          ))}
        </div>

        {/* 积分消耗动画条 */}
        <div className="bg-white rounded-2xl p-4 mb-6">
          <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
            <span>积分消耗</span>
            <span className="text-emerald-600 font-semibold">{totalPoints.toLocaleString()} 积分</span>
          </div>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-emerald-400 to-emerald-500 rounded-full"
              style={{
                width: phase >= 3 ? `${Math.min((totalPoints / (totalPoints + remainPoints)) * 100, 100)}%` : '0%',
                transition: 'width 1s ease 0.5s',
              }}
            />
          </div>
        </div>

        {/* 按钮 */}
        <button
          onClick={() => navigate('/')}
          className="w-full bg-emerald-500 text-white py-3.5 rounded-full font-medium cursor-pointer whitespace-nowrap mb-3 active:scale-95 transition-transform"
        >
          返回首页
        </button>
        <button
          onClick={() => navigate('/orders')}
          className="w-full border border-gray-200 text-gray-600 py-3.5 rounded-full font-medium cursor-pointer whitespace-nowrap active:scale-95 transition-transform"
        >
          查看我的订单
        </button>
      </div>

      <style>{`
        @keyframes particleFly {
          0% { transform: translate(-50%, -50%) rotate(var(--angle, 0deg)) translateY(0); opacity: 1; }
          100% { transform: translate(-50%, -50%) rotate(var(--angle, 0deg)) translateY(-80px); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default SuccessScreen;
